// ─────────────────────────────────────────────────────────────────────────────
// Mock data — Course Evaluations Dashboard
// ─────────────────────────────────────────────────────────────────────────────

import {
  classifyTermTimelinePosition,
  type TermTimelinePosition,
} from "@/lib/evaluation-term-utils"

export type EvaluationStage =
  | "notSetup"
  | "draft"
  | "scheduled"
  | "live"
  | "closed"
  | "published"

export interface EvaluationCounts {
  notSetup: number
  draft: number
  scheduled: number
  live: number
  closed: number
  published: number
}

export interface TermRatings {
  programAverage: number
  courseAverage: number
  facultyAverage: number
  otherAverage?: number
}

export interface TermAttention {
  coursesBelowThreshold: number
  facultyRolesBelowThreshold: number
}

export type TermCardCase =
  | "no-terms"
  | "no-courses"
  | "no-evaluations"
  | "mixed"
  | "all-draft-or-above"
  | "all-scheduled-or-above"
  | "all-live-or-above"
  | "all-closed"
  | "all-published"

export interface EvaluationTerm {
  id: string
  programName: string
  academicYear: string
  termName: string
  startDate: string
  endDate: string
  timelinePosition: TermTimelinePosition
  courseOfferings: number
  counts: EvaluationCounts
  /** Average response rate for live evaluations (0–100) */
  liveResponseRate?: number
  /** Average response rate for closed evaluations (0–100) */
  closedResponseRate?: number
  /** Nearest scheduled send date */
  nextScheduledDate?: string
  /** Nearest live closing date */
  nextClosingDate?: string
  ratings?: TermRatings
  attention?: TermAttention
  cardCase: TermCardCase
  needsAttention?: boolean
}

/** Reference date for mock timeline — Aug 23, 2025 per user context */
export const EVALUATION_REFERENCE_DATE = new Date("2025-08-23")

export const MOCK_EVALUATION_TERMS: EvaluationTerm[] = [
  // Current term — Case 4: mixed evaluation states (Summit PA)
  {
    id: "fall-2025",
    programName: "Summit PA",
    academicYear: "2025-2026",
    termName: "Fall 2025",
    startDate: "2025-08-18",
    endDate: "2025-12-12",
    timelinePosition: "current",
    courseOfferings: 50,
    counts: {
      notSetup: 5,
      draft: 3,
      scheduled: 7,
      live: 30,
      closed: 5,
      published: 0,
    },
    liveResponseRate: 54,
    closedResponseRate: 70,
    nextScheduledDate: "2025-08-30",
    nextClosingDate: "2025-08-30",
    cardCase: "mixed",
  },
  // Last term — Case 8: all closed with analytics (Lakeside OT)
  {
    id: "summer-2025",
    programName: "Lakeside OT",
    academicYear: "2024-2025",
    termName: "Summer 2025",
    startDate: "2025-05-12",
    endDate: "2025-07-25",
    timelinePosition: "last",
    courseOfferings: 42,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 42,
      published: 0,
    },
    closedResponseRate: 78,
    ratings: {
      programAverage: 4.2,
      courseAverage: 4.1,
      facultyAverage: 4.4,
      otherAverage: 4.0,
    },
    attention: {
      coursesBelowThreshold: 3,
      facultyRolesBelowThreshold: 2,
    },
    cardCase: "all-closed",
    needsAttention: true,
  },
  // Upcoming — Case 3: courses but no evaluations (Spring 2026)
  {
    id: "spring-2026",
    programName: "Summit PA",
    academicYear: "2025-2026",
    termName: "Spring 2026",
    startDate: "2026-01-12",
    endDate: "2026-05-08",
    timelinePosition: "upcoming",
    courseOfferings: 48,
    counts: {
      notSetup: 48,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 0,
    },
    cardCase: "no-evaluations",
  },
  // Future — Case 2: term setup, no courses (Fall 2026)
  {
    id: "fall-2026",
    programName: "Riverside DPT",
    academicYear: "2026-2027",
    termName: "Fall 2026",
    startDate: "2026-08-24",
    endDate: "2026-12-18",
    timelinePosition: "future",
    courseOfferings: 0,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 0,
    },
    cardCase: "no-courses",
  },
  // Past terms for analytics table
  {
    id: "spring-2025",
    programName: "Summit PA",
    academicYear: "2024-2025",
    termName: "Spring 2025",
    startDate: "2025-01-13",
    endDate: "2025-05-09",
    timelinePosition: "past",
    courseOfferings: 46,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 46,
    },
    closedResponseRate: 30,
    ratings: {
      programAverage: 4.3,
      courseAverage: 4.2,
      facultyAverage: 4.5,
    },
    cardCase: "all-published",
  },
  {
    id: "fall-2024",
    programName: "Lakeside OT",
    academicYear: "2024-2025",
    termName: "Fall 2024",
    startDate: "2024-08-19",
    endDate: "2024-12-13",
    timelinePosition: "past",
    courseOfferings: 40,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 40,
    },
    closedResponseRate: 40,
    ratings: {
      programAverage: 4.1,
      courseAverage: 4.0,
      facultyAverage: 4.3,
    },
    cardCase: "all-published",
  },
  {
    id: "spring-2024",
    programName: "Riverside DPT",
    academicYear: "2023-2024",
    termName: "Spring 2024",
    startDate: "2024-01-08",
    endDate: "2024-05-03",
    timelinePosition: "past",
    courseOfferings: 38,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 38,
    },
    closedResponseRate: 71,
    ratings: {
      programAverage: 3.9,
      courseAverage: 3.8,
      facultyAverage: 4.0,
    },
    cardCase: "all-published",
  },
  {
    id: "summer-2024",
    programName: "Lakeside OT",
    academicYear: "2023-2024",
    termName: "Summer 2024",
    startDate: "2024-05-13",
    endDate: "2024-07-26",
    timelinePosition: "past",
    courseOfferings: 36,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 36,
    },
    closedResponseRate: 74,
    ratings: {
      programAverage: 4.0,
      courseAverage: 3.9,
      facultyAverage: 4.2,
    },
    attention: {
      coursesBelowThreshold: 1,
      facultyRolesBelowThreshold: 1,
    },
    cardCase: "all-published",
  },
  {
    id: "fall-2023",
    programName: "Summit PA",
    academicYear: "2023-2024",
    termName: "Fall 2023",
    startDate: "2023-08-21",
    endDate: "2023-12-15",
    timelinePosition: "past",
    courseOfferings: 44,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 44,
    },
    closedResponseRate: 68,
    ratings: {
      programAverage: 3.8,
      courseAverage: 3.7,
      facultyAverage: 3.9,
    },
    cardCase: "all-published",
  },
  {
    id: "spring-2023",
    programName: "Riverside DPT",
    academicYear: "2022-2023",
    termName: "Spring 2023",
    startDate: "2023-01-09",
    endDate: "2023-05-05",
    timelinePosition: "past",
    courseOfferings: 35,
    counts: {
      notSetup: 0,
      draft: 0,
      scheduled: 0,
      live: 0,
      closed: 0,
      published: 35,
    },
    closedResponseRate: 65,
    ratings: {
      programAverage: 3.7,
      courseAverage: 3.6,
      facultyAverage: 3.8,
    },
    cardCase: "all-published",
  },
]

export function resolveEvaluationTerm(
  term: EvaluationTerm,
  today: Date = EVALUATION_REFERENCE_DATE,
): EvaluationTerm {
  const timelinePosition = classifyTermTimelinePosition(
    today,
    new Date(term.startDate),
    new Date(term.endDate),
  )
  if (timelinePosition === term.timelinePosition) return term
  return { ...term, timelinePosition }
}

const DEMO_CASE_BASE: Pick<
  EvaluationTerm,
  "programName" | "academicYear" | "termName" | "startDate" | "endDate" | "courseOfferings"
> = {
  programName: "Summit PA",
  academicYear: "2025-2026",
  termName: "Fall 2025",
  startDate: "2025-08-18",
  endDate: "2025-12-12",
  courseOfferings: 50,
}

function buildDemoCaseTerm(
  id: string,
  cardCase: TermCardCase,
  counts: EvaluationCounts,
): EvaluationTerm {
  return resolveEvaluationTerm({
    id,
    ...DEMO_CASE_BASE,
    timelinePosition: "current",
    counts,
    cardCase,
    liveResponseRate: counts.live > 0 ? 54 : undefined,
    closedResponseRate: counts.closed > 0 ? 70 : undefined,
    nextScheduledDate: counts.scheduled > 0 ? "2025-08-30" : undefined,
    nextClosingDate: counts.live > 0 ? "2025-08-30" : undefined,
  })
}

export const DEMO_CASE_5_TERM = buildDemoCaseTerm("fall-2025-case5", "all-draft-or-above", {
  notSetup: 0,
  draft: 3,
  scheduled: 7,
  live: 30,
  closed: 5,
  published: 0,
})

export const DEMO_CASE_6_TERM = buildDemoCaseTerm("fall-2025-case6", "all-scheduled-or-above", {
  notSetup: 0,
  draft: 0,
  scheduled: 7,
  live: 30,
  closed: 5,
  published: 0,
})

export const DEMO_CASE_7_TERM = buildDemoCaseTerm("fall-2025-case7", "all-live-or-above", {
  notSetup: 0,
  draft: 0,
  scheduled: 0,
  live: 30,
  closed: 5,
  published: 0,
})

/** Mid term: schedules set, collection just starting (few live, many scheduled). */
export const DEMO_CASE_MIDWAY_TERM = {
  ...buildDemoCaseTerm("fall-2025-midway", "mixed", {
    notSetup: 0,
    draft: 0,
    scheduled: 38,
    live: 10,
    closed: 2,
    published: 0,
  }),
  liveResponseRate: 32,
  nextScheduledDate: "2025-08-28",
  nextClosingDate: "2025-09-06",
}

/** State 2: term created, configuration in progress. */
export const DEMO_STATE_SETUP_TERM = buildDemoCaseTerm("fall-2026-setup", "mixed", {
  notSetup: 3,
  draft: 8,
  scheduled: 12,
  live: 0,
  closed: 0,
  published: 0,
})

/** State 3: fully configured, awaiting launch. */
export const DEMO_STATE_READY_TERM = {
  ...buildDemoCaseTerm("fall-2026-ready", "all-scheduled-or-above", {
    notSetup: 0,
    draft: 0,
    scheduled: 48,
    live: 0,
    closed: 0,
    published: 0,
  }),
  nextScheduledDate: "2025-09-14",
}

/** State 4: first live collection, no prior term. */
export const DEMO_STATE_FIRST_LIVE_TERM = {
  ...buildDemoCaseTerm("fall-2026-live", "mixed", {
    notSetup: 0,
    draft: 0,
    scheduled: 8,
    live: 30,
    closed: 0,
    published: 0,
  }),
  liveResponseRate: 54,
  nextClosingDate: "2025-08-30",
}

/** State 5: first closed cycle (no comparison history). */
export const DEMO_STATE_FIRST_CLOSED_TERM = resolveEvaluationTerm({
  id: "fall-2025-first-closed",
  programName: "Summit PA",
  academicYear: "2025-2026",
  termName: "Fall 2025",
  startDate: "2025-08-18",
  endDate: "2025-12-12",
  timelinePosition: "last",
  courseOfferings: 48,
  counts: {
    notSetup: 0,
    draft: 0,
    scheduled: 0,
    live: 0,
    closed: 48,
    published: 0,
  },
  closedResponseRate: 78,
  ratings: {
    programAverage: 4.1,
    courseAverage: 4.1,
    facultyAverage: 4.4,
  },
  attention: {
    coursesBelowThreshold: 3,
    facultyRolesBelowThreshold: 0,
  },
  cardCase: "all-closed",
})

function allResolvedTerms(today: Date = EVALUATION_REFERENCE_DATE): EvaluationTerm[] {
  return MOCK_EVALUATION_TERMS.map(term => resolveEvaluationTerm(term, today))
}

export function getTermsByPosition(position: TermTimelinePosition): EvaluationTerm[] {
  return allResolvedTerms().filter(t => t.timelinePosition === position)
}

export function getPastTerms(): EvaluationTerm[] {
  return allResolvedTerms()
    .filter(t => t.timelinePosition === "past")
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
}

const TERM_TREND_MAX = 6

function termResponseRateValue(term: EvaluationTerm): number | undefined {
  if (term.timelinePosition === "current" && term.liveResponseRate != null) {
    return term.liveResponseRate
  }
  return term.closedResponseRate
}

/** Chronological closed and live terms for response rate trend charts. */
export function getTermResponseTrend(
  options: {
    includeCurrent?: boolean
    includeLast?: boolean
    maxTerms?: number
  } = {},
): EvaluationTerm[] {
  const { includeCurrent = true, includeLast = true, maxTerms = TERM_TREND_MAX } = options

  return allResolvedTerms().filter(term => {
    if (term.timelinePosition === "upcoming" || term.timelinePosition === "future") {
      return false
    }
    if (term.timelinePosition === "current" && !includeCurrent) return false
    if (term.timelinePosition === "last" && !includeLast) return false
    return termResponseRateValue(term) != null
  })
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .slice(-maxTerms)
}

/** Chronological terms with program ratings for score trend charts. */
export function getTermRatingTrend(maxTerms = TERM_TREND_MAX): EvaluationTerm[] {
  return MOCK_EVALUATION_TERMS.filter(
    term =>
      term.ratings?.programAverage != null &&
      (term.timelinePosition === "past" ||
        term.timelinePosition === "last" ||
        term.timelinePosition === "current"),
  )
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .slice(-maxTerms)
}

/** Terms with below-threshold flags for trend charts. */
export function getTermAttentionTrend(maxTerms = TERM_TREND_MAX): EvaluationTerm[] {
  return MOCK_EVALUATION_TERMS.filter(term => term.attention != null)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .slice(-maxTerms)
}

export function shortTermChartLabel(termName: string): string {
  const match = termName.match(/^(\w+)\s+(\d{4})$/)
  if (!match) return termName
  return `${match[1]} '${match[2].slice(-2)}`
}

export function getTermById(termId: string): EvaluationTerm | undefined {
  const term = MOCK_EVALUATION_TERMS.find(row => row.id === termId)
  if (term) return resolveEvaluationTerm(term)
  if (termId === DEMO_CASE_5_TERM.id) return DEMO_CASE_5_TERM
  if (termId === DEMO_CASE_6_TERM.id) return DEMO_CASE_6_TERM
  if (termId === DEMO_CASE_7_TERM.id) return DEMO_CASE_7_TERM
  if (termId === DEMO_CASE_MIDWAY_TERM.id) return DEMO_CASE_MIDWAY_TERM
  if (termId === DEMO_STATE_SETUP_TERM.id) return DEMO_STATE_SETUP_TERM
  if (termId === DEMO_STATE_READY_TERM.id) return DEMO_STATE_READY_TERM
  if (termId === DEMO_STATE_FIRST_LIVE_TERM.id) return DEMO_STATE_FIRST_LIVE_TERM
  if (termId === DEMO_STATE_FIRST_CLOSED_TERM.id) return DEMO_STATE_FIRST_CLOSED_TERM
  return undefined
}

/** Demo flag: `?demo=empty` on the dashboard shows Case 1 (no terms configured). */
export function isEmptyDashboardDemo(search: string): boolean {
  if (typeof search !== "string") return false
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
  return params.get("demo") === "empty"
}

export function evaluationCoveragePercent(counts: EvaluationCounts, total: number): number {
  if (total === 0) return 0
  const covered =
    counts.scheduled + counts.live + counts.closed + counts.published
  return Math.round((covered / total) * 100)
}

export function evaluationCoverageCount(counts: EvaluationCounts): number {
  return counts.scheduled + counts.live + counts.closed + counts.published
}

export function totalEvaluationCourses(counts: EvaluationCounts): number {
  return (
    counts.notSetup +
    counts.draft +
    counts.scheduled +
    counts.live +
    counts.closed +
    counts.published
  )
}

export function formatStatusSummary(counts: EvaluationCounts): string {
  const parts: string[] = []
  if (counts.live > 0) parts.push(`${counts.live} live`)
  if (counts.scheduled > 0) parts.push(`${counts.scheduled} scheduled`)
  if (counts.closed > 0) parts.push(`${counts.closed} closed`)
  if (counts.published > 0) parts.push(`${counts.published} published`)
  if (counts.draft > 0) parts.push(`${counts.draft} draft`)
  if (counts.notSetup > 0) parts.push(`${counts.notSetup} not set up`)
  const total = totalEvaluationCourses(counts)
  return parts.length > 0 ? `${parts.join(" · ")} · ${total} total` : `${total} total`
}
